import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import { Card } from '../../components/UI';
import { HiOutlineChatAlt, HiOutlineCog, HiOutlineTable, HiOutlineViewList, HiOutlineLink, HiOutlineCreditCard, HiOutlineDocumentReport, HiOutlineSwitchHorizontal, HiOutlineMicrophone, HiOutlineMail, HiOutlinePhone, HiOutlineCalendar, HiOutlineLocationMarker } from 'react-icons/hi';
import WhatsAppTemplates from './WhatsAppTemplates';
import EmailTemplates from './EmailTemplates';
import Templates from './Templates';
import SMSTemplates from './SMSTemplates';
import GoogleSheetIntegration from './GoogleSheetIntegration';
import PipelineStages from './PipelineStages';
import Integrations from './Integrations';
import Subscription from './Subscription';
import ReportSettings from './ReportSettings';
import AssignmentRules from './AssignmentRules';
import CallSettings from './CallSettings';
import CalendarTab from './CalendarTab';
import VisitSettings from './VisitSettings';
// RecordingControls tab disabled (commented out — not removed)
// import RecordingControls from './RecordingControls';
// CallTargets tab moved to Users page (Assign Target button)
// import CallTargets from './CallTargets';

const tabs = [
  { id: 'subscription', label: 'Plan & Billing', icon: HiOutlineCreditCard },
  { id: 'calls', label: 'Calls', icon: HiOutlinePhone },
  // WhatsApp / Email templates merged into one "Templates" section
  // (with the new SMS template type selectable inside it).
  { id: 'templates', label: 'Templates', icon: HiOutlineChatAlt },
  // { id: 'whatsapp-templates', label: 'WhatsApp Templates', icon: HiOutlineChatAlt },
  // { id: 'email-templates', label: 'Email Templates', icon: HiOutlineMail },
  { id: 'google-sheet', label: 'Google Sheets', icon: HiOutlineTable },
  { id: 'pipeline-stages', label: 'Pipeline Stages', icon: HiOutlineViewList },
  { id: 'integrations', label: 'Integrations', icon: HiOutlineLink },
  { id: 'assignment-rules', label: 'Auto-Assign', icon: HiOutlineSwitchHorizontal },
  // { id: 'recording-controls', label: 'Recordings', icon: HiOutlineMicrophone },
  { id: 'daily-report', label: 'Daily Report', icon: HiOutlineDocumentReport },
  { id: 'calendar', label: 'Calendar', icon: HiOutlineCalendar },
  { id: 'visits', label: 'Visits', icon: HiOutlineLocationMarker },
  // { id: 'call-targets', label: 'Call Targets', icon: HiOutlinePhone },
];

// Old per-channel template tabs — no longer in the tab bar but still valid URLs.
const TAB_ALIASES = ['whatsapp-templates', 'email-templates', 'sms-templates'];
const VALID_TABS = new Set([...tabs.map((t) => t.id), ...TAB_ALIASES]);

const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tabsScrollRef = useRef(null);

  // Get active tab from URL
  const getActiveTab = () => {
    const path = location.pathname.split('/').pop();
    if (path && path !== 'settings') {
      return path;
    }
    return 'subscription';
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());

  // Update active tab when URL changes; unknown tabs redirect to Plan & Billing
  useEffect(() => {
    const newTab = getActiveTab();
    if (!VALID_TABS.has(newTab)) {
      navigate('/app/settings/subscription', { replace: true });
      return;
    }
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Keep the active tab visible in the horizontally-scrolling tab strip (small screens)
  useEffect(() => {
    const container = tabsScrollRef.current;
    const activeBtn = container?.querySelector('[aria-current="page"]');
    if (!container || !activeBtn || container.scrollWidth <= container.clientWidth) return;
    const btnLeft = activeBtn.offsetLeft; // container is position:relative, so this is relative to it
    const btnRight = btnLeft + activeBtn.offsetWidth;
    if (btnLeft < container.scrollLeft || btnRight > container.scrollLeft + container.clientWidth) {
      container.scrollTo({ left: Math.max(0, btnLeft - 16), behavior: 'smooth' });
    }
  }, [activeTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/app/settings/${tabId}`, { replace: true });
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'subscription':
        return <Subscription />;
      case 'calls':
        return <CallSettings />;
      case 'templates':
        return <Templates />;
      // Old separate template tabs (kept, now routed through the merged
      // Templates section above — reachable only via direct URL):
      case 'whatsapp-templates':
        return <WhatsAppTemplates />;
      case 'email-templates':
        return <EmailTemplates />;
      case 'sms-templates':
        return <SMSTemplates />;
      case 'google-sheet':
        return <GoogleSheetIntegration />;
      case 'pipeline-stages':
        return <PipelineStages />;
      case 'integrations':
        return <Integrations />;
      case 'assignment-rules':
        return <AssignmentRules />;
      // case 'recording-controls':
      //   return <RecordingControls />;
      case 'daily-report':
        return <ReportSettings />;
      case 'calendar':
        return <CalendarTab />;
      case 'visits':
        return <VisitSettings />;
      // case 'call-targets':
      //   return <CallTargets />;
      default:
        return <Subscription />;
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
            <HiOutlineCog className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-500 text-xs sm:text-sm">Manage your application settings</p>
          </div>
        </div>

        {/* Tabs Navigation */}
        {/* Below lg: single horizontally-scrolling row. lg+: wraps so every tab is reachable. */}
        <div ref={tabsScrollRef} className="relative border-b border-gray-200 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide lg:overflow-visible">
          <nav className="flex gap-2 sm:gap-4 min-w-max lg:min-w-0 lg:flex-wrap lg:gap-x-1 lg:gap-y-0" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                    ${isActive
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.id === 'templates' ? 'Templates' : tab.id === 'whatsapp-templates' ? 'WhatsApp' : tab.id === 'email-templates' ? 'Email' : tab.id === 'google-sheet' ? 'Sheets' : tab.id === 'pipeline-stages' ? 'Pipeline' : tab.id === 'integrations' ? 'Links' : tab.id === 'daily-report' ? 'Report' : tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
          {renderContent()}
        </div>
      </div>
    </Layout>
  );
};

export default Settings;